/**
 * Modal "Restablecer contraseña" (admin → otro usuario).
 *
 * Existe por las cuentas de código de empleado: su correo es interno y no
 * entregable, así que el flujo por email no les llega. El admin escribe una
 * clave nueva y se la entrega a la persona por un canal privado. El cambio lo
 * hace el servidor (`reset_user_password`): API admin, revoca sesiones y queda
 * auditado sin la contraseña.
 */
import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import { passwordPolicyErrors } from '../../utils/passwordPolicy';

const ResetPasswordModal = ({ show, user, onHide, onSubmit, submitting = false }) => {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState(null);

  // Cada apertura empieza limpia: nunca dejar una clave escrita en memoria
  // de la pantalla entre un usuario y otro.
  useEffect(() => {
    if (!show) {
      setPassword('');
      setConfirm('');
      setShowPw(false);
      setError(null);
    }
  }, [show]);

  const faltan = passwordPolicyErrors(password);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (faltan.length) {
      setError(`La contraseña no cumple: ${faltan.join(', ').toLowerCase()}.`);
      return;
    }
    if (password !== confirm) {
      setError('Las contraseñas no coinciden.');
      return;
    }
    const res = await onSubmit(password);
    if (res && res.error) setError(res.error.message || 'No se pudo restablecer la contraseña.');
  };

  return (
    <Modal show={show} onHide={onHide} centered data-testid="reset-password-modal">
      <Form onSubmit={handleSubmit} noValidate>
        <Modal.Header closeButton>
          <Modal.Title>Restablecer contraseña</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-3">
            Nueva contraseña para <strong>{user?.full_name || 'este usuario'}</strong>.
            Se cierran sus sesiones abiertas. Entregásela por un canal privado.
          </p>
          {error && <Alert variant="danger" data-testid="reset-password-error">{error}</Alert>}
          <Form.Group className="mb-3" controlId="reset-password-new">
            <Form.Label>Contraseña nueva</Form.Label>
            <Form.Control
              type={showPw ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              disabled={submitting}
              data-testid="reset-password-input"
            />
            <Form.Text muted>Mínimo 8 caracteres, con mayúscula, minúscula y número.</Form.Text>
          </Form.Group>
          <Form.Group className="mb-2" controlId="reset-password-confirm">
            <Form.Label>Repetir contraseña</Form.Label>
            <Form.Control
              type={showPw ? 'text' : 'password'}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              disabled={submitting}
              data-testid="reset-password-confirm"
            />
          </Form.Group>
          <Form.Check
            type="checkbox"
            id="reset-password-show"
            label="Mostrar"
            checked={showPw}
            onChange={(e) => setShowPw(e.target.checked)}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={submitting}>Cancelar</Button>
          <Button variant="warning" type="submit" disabled={submitting} data-testid="reset-password-submit">
            {submitting ? <Spinner size="sm" animation="border" /> : 'Restablecer'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default ResetPasswordModal;
